import React from "react";
import styled from "styled-components";
import {
  requestAnimFrame,
  getGesturePointFromEvent,
  preventEvent,
  logEvent
} from "../../utilities";

const Wrapper = styled.aside`
  --arrow-size: 27px;

  position: relative;
  overflow: hidden;
  margin: 0 calc(var(--gutter) / 2);
  padding: 0 calc(var(--arrow-size) + var(--gutter) / 2);
  user-select: none;
  touch-action: pan-y;
  display: none; // CHROME 41

  @supports (display: grid) {
    display: block;
  }
`;

const Arrow = styled.button`
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  width: calc(var(--gutter) + var(--arrow-size));
  border: 0;
  z-index: 10;
  padding: 0;
  cursor: ${p => (p.disabled ? "auto" : "pointer")};

  &:hover {
    &::after {
      --scale: 1.2;
    }
  }

  &::after {
    content: "";
    width: var(--arrow-size);
    height: var(--arrow-size);
    display: block;
    position: absolute;
    top: 50%;
    margin-top: calc(var(--arrow-size) / -2);
    border: 0 solid transparent;
    border-top: 5px solid;
    border-color: #f0f0f0;
    transform: translateX(var(--offset)) scale(var(--scale, 1))
      rotate(var(--rotate));
    transition: all ease 200ms;
    opacity: ${p => (p.disabled ? 0 : 1)};
  }

  &:focus {
    outline: none;
  }
`;

const Prev = styled(Arrow)`
  background: linear-gradient(to right, #fff 45%, rgba(255, 255, 255, 0));
  left: 0;

  &::after {
    --offset: ${p => (p.disabled ? -8 : 0)}px;
    --rotate: -45deg;
    border-left-width: 5px;
    right: 0;
  }
`;

const Next = styled(Arrow)`
  background: linear-gradient(to left, #fff 45%, rgba(255, 255, 255, 0));
  right: 0;

  &::after {
    --offset: ${p => (p.disabled ? 8 : 0)}px;
    --rotate: 45deg;
    border-right-width: 5px;
    left: 0;
  }
`;

const Grid = styled.div`
  --cell-width: var(--product-width, 200px);

  display: flex;
  align-items: stretch;
  transition: all ease 200ms;
  transform: translateX(
    calc((var(--cell-width) + var(--gutter)) * ${p => p.page - 1 || 0} * -1)
  );
`;

const El = styled.div`
  margin: calc(var(--gutter) / 2);
  flex: 0 0 var(--cell-width);
  display: flex;
  align-items: stretch;
`;

class Slider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      tilePerPage: 1
    };
    this.cellw = null;
    this.gutter = null;
    this.rafPending = false;
    this.initialTouchPos = null;
    this.lastTouchPos = null;
    this.lastOffset = 0;
    this.currentXPosition = 0;
    this.isClickLocked = false;

    this.onResize = this.onResize.bind(this);
    this.onGestureStart = this.onGestureStart.bind(this);
    this.onGestureMove = this.onGestureMove.bind(this);
    this.onGestureEnd = this.onGestureEnd.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.updateSwipeRestPosition = this.updateSwipeRestPosition.bind(this);
    this.animFrame = this.animFrame.bind(this);
    this.lockClick = this.lockClick.bind(this);
    this.unlockClick = this.unlockClick.bind(this);
  }

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener("resize", this.onResize, false);

    // Removed for different browser implementatios
    // https://github.com/w3c/pointerevents/issues/75#issuecomment-376833176
    // if (false && window.PointerEvent) {
    //   this.wrapper.addEventListener("pointerdown", this.onGestureStart, true);
    //   this.wrapper.addEventListener("pointermove", this.onGestureMove, true);
    //   this.wrapper.addEventListener("pointerup", this.onGestureEnd, true);
    //   this.wrapper.addEventListener("pointercancel", this.onGestureEnd, true);
    // } else {
    this.wrapper.addEventListener("mousedown", this.onGestureStart, {
      passive: true
    });
    this.wrapper.addEventListener("touchstart", this.onGestureStart, {
      passive: true
    });
    this.wrapper.addEventListener("touchmove", this.onGestureMove, {
      passive: true
    });
    this.wrapper.addEventListener("touchend", this.onGestureEnd, {
      passive: true
    });
    this.wrapper.addEventListener("touchcancel", this.onGestureEnd, {
      passive: true
    });
    // }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
  }

  onResize() {
    if (this.rqf) return;
    this.rqf = window.requestAnimationFrame(() => {
      delete this.rqf;
      this.updateDimensions();
      this.goTo(0);
    });
  }

  onGestureStart(evt) {
    if (evt.type === "mousedown") evt.preventDefault();
    if (evt.touches && evt.touches.length > 1) return;

    // Removed for different browser implementatios
    // https://github.com/w3c/pointerevents/issues/75#issuecomment-376833176
    // if (false && window.PointerEvent) {
    //   this.wrapper.setPointerCapture(evt.pointerId);
    // } else {
    document.addEventListener("mousemove", this.onGestureMove, true);
    document.addEventListener("mouseup", this.onGestureEnd, true);
    // }
    this.initialTouchPos = getGesturePointFromEvent(evt);
    this.lastTouchPos = getGesturePointFromEvent(evt);
    this.lastOffset = 0;
    this.grid.style.transition = "initial";
  }

  onGestureMove(evt) {
    if (evt.type === "mousemove") evt.preventDefault();
    if (!this.initialTouchPos || this.rafPending) return;
    const { x: lastX } = this.lastTouchPos;

    this.lockClick();
    this.lastTouchPos = getGesturePointFromEvent(evt);
    this.lastOffset = lastX - this.lastTouchPos.x;
    this.rafPending = true;
    requestAnimFrame(this.animFrame);
  }

  onGestureEnd(evt) {
    if (evt.type === "mouseup") evt.preventDefault();
    if (!this.initialTouchPos) return;

    // Removed for different browser implementatios
    // https://github.com/w3c/pointerevents/issues/75#issuecomment-376833176
    // if (false && window.PointerEvent) {
    //   this.wrapper.releasePointerCapture(evt.pointerId);
    // } else {
    document.removeEventListener("mousemove", this.onGestureMove, true);
    document.removeEventListener("mouseup", this.onGestureEnd, true);
    // }
    this.rafPending = false;
    setTimeout(this.unlockClick, 0);
    this.updateSwipeRestPosition();
    this.initialTouchPos = null;
  }

  get lastPage() {
    const last = this.props.products.length - this.state.tilePerPage + 1;
    return last > 1 ? last : 1;
  }

  lockClick() {
    if (!this.isClickLocked) {
      this.wrapper.addEventListener("click", preventEvent, true);
      this.isClickLocked = true;
    }
  }

  unlockClick() {
    if (this.isClickLocked) {
      this.wrapper.removeEventListener("click", preventEvent, true);
      this.isClickLocked = false;
    }
  }

  updateSwipeRestPosition() {
    const differenceInX = this.initialTouchPos.x - this.lastTouchPos.x;
    const cellDiff = differenceInX / this.cellw;
    this.currentXPosition -= differenceInX;
    this.grid.style.transform = "";
    if (this.lastOffset > 10) this.goTo(Math.ceil(cellDiff));
    else if (this.lastOffset < -10) this.goTo(Math.floor(cellDiff));
    else this.goTo(Math.round(cellDiff));
    this.currentXPosition =
      (this.cellw + this.gutter) * (this.state.page - 1) * -1;

    this.grid.style.transition = "";
  }

  animFrame() {
    if (!this.rafPending) return;

    const differenceInX = this.initialTouchPos.x - this.lastTouchPos.x;
    const newXTransform = this.currentXPosition - differenceInX;
    this.grid.style.transform = `translateX(${newXTransform}px)`;
    this.rafPending = false;
  }

  updateDimensions() {
    const { width } = this.grid.getBoundingClientRect();
    const styles = getComputedStyle(this.grid);
    this.cellw = parseInt(styles.getPropertyValue("--cell-width"), 10);
    this.gutter = parseInt(styles.getPropertyValue("--gutter"), 10);
    this.setState({
      tilePerPage: Math.floor(width / (this.cellw + this.gutter))
    });
  }

  goTo(offset) {
    this.setState(state => {
      let page = state.page + offset;
      if (page <= 0) page = 1;
      if (page > this.lastPage) page = this.lastPage;
      this.currentXPosition = (this.cellw + this.gutter) * (page - 1) * -1;
      if (state.page !== page) {
        logEvent({
          category: "slider",
          action: "moved",
          label: page
        });
      }
      return { page };
    });
  }

  render() {
    const { page } = this.state;
    const { products, renderProd } = this.props;

    return (
      <Wrapper className={this.props.className}>
        <div
          ref={el => {
            this.wrapper = el;
          }}
        >
          <h2 className="ninja">I prodotti</h2>
          <Grid
            page={page}
            ref={el => {
              this.grid = el;
            }}
          >
            {products.map(prod => (
              <El key={prod.imageId}>{renderProd(prod)}</El>
            ))}
          </Grid>
        </div>
        <Prev
          onClick={() => this.goTo(-1)}
          disabled={page <= 1}
          aria-label="Visualizza i prodotti precedenti"
        />
        <Next
          onClick={() => this.goTo(1)}
          disabled={page >= this.lastPage}
          aria-label="Visualizza i prodotti successivi"
        />
      </Wrapper>
    );
  }
}

export default Slider;
